import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { API_URL } from '../config/api';
import { useTranslation } from 'react-i18next';
import './StripePayment.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51QJxXx...');

const PAYMENT_RETURN_KEY = 'stripePaymentReturn';

const CheckoutForm = ({
  amount,
  paymentIntentId,
  returnUrl,
  restaurantId,
  onSuccess,
  onError,
  restaurantName
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const paymentProcessedRef = useRef(false);
  const successCallbackCalledRef = useRef(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (paymentProcessedRef.current || processing || paymentSucceeded) return;
    if (!stripe || !elements) return;

    paymentProcessedRef.current = true;
    setProcessing(true);
    setError(null);

    try {
      // Store context for redirect return (bank payments)
      if (paymentIntentId && restaurantId) {
        sessionStorage.setItem(PAYMENT_RETURN_KEY, JSON.stringify({
          paymentIntentId,
          restaurantId
        }));
      }

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        paymentProcessedRef.current = false;
        setError(stripeError.message);
        setProcessing(false);
        if (onError) onError(stripeError.message);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        setProcessing(false);
        setPaymentSucceeded(true);
        sessionStorage.removeItem(PAYMENT_RETURN_KEY);
        if (onSuccess && !successCallbackCalledRef.current) {
          successCallbackCalledRef.current = true;
          onSuccess(paymentIntent);
        }
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // Redirect-based method: user was sent to bank; they will return to return_url
        // Nothing to do here – page may have already redirected
        setProcessing(false);
        paymentProcessedRef.current = false;
      } else {
        paymentProcessedRef.current = false;
        setProcessing(false);
        const statusError = `Payment status: ${paymentIntent?.status || 'unknown'}`;
        setError(statusError);
        if (onError) onError(statusError);
      }
    } catch (err) {
      console.error('Payment submission error:', err);
      paymentProcessedRef.current = false;
      setProcessing(false);
      const errorMessage = err.message || 'An error occurred during payment';
      setError(errorMessage);
      if (onError) onError(errorMessage);
    }
  };

  const isButtonDisabled = !stripe || !elements || processing || paymentSucceeded;

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="stripe-payment-element-wrap">
        <PaymentElement
          options={{
            layout: 'tabs',
            radios: true,
            spacing: 'compact'
          }}
          onChange={(event) => {
            setError(event.error ? event.error.message : null);
          }}
        />
      </div>
      {error && <div className="stripe-error">{error}</div>}
      <button
        type="submit"
        disabled={isButtonDisabled}
        className="btn btn-primary stripe-submit-btn"
      >
        {processing
          ? (t('payment.processing') || 'Processing...')
          : paymentSucceeded
            ? (t('payment.paid') || 'Paid')
            : (t('payment.pay') || `Pay €${amount.toFixed(2)}`)}
      </button>
    </form>
  );
};

const StripePayment = ({ amount, onSuccess, onError, restaurantName, restaurantId }) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const createdRef = useRef(false);
  const idempotencyKeyRef = useRef(null);

  if (idempotencyKeyRef.current === null) {
    idempotencyKeyRef.current =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `pi-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  useEffect(() => {
    let cancelled = false;

    const createPaymentIntent = async () => {
      if (createdRef.current || amount <= 0) return;
      createdRef.current = true;
      setIsLoading(true);

      try {
        const response = await axios.post(
          `${API_URL}/payments/create-payment-intent`,
          {
            amount,
            currency: 'eur',
            idempotencyKey: idempotencyKeyRef.current,
            metadata: { restaurantName: restaurantName || '' }
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        if (!cancelled) {
          setClientSecret(response.data.clientSecret);
          setPaymentIntentId(response.data.paymentIntentId || '');
          setError(null);
        }
      } catch (err) {
        console.error('Error creating payment intent:', err);
        if (!cancelled) {
          createdRef.current = false;
          setError(err.response?.data?.message || 'Failed to initialize payment');
          if (onError) onError(err.response?.data?.message || 'Failed to initialize payment');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    createPaymentIntent();
    return () => { cancelled = true; };
  }, [amount, restaurantName, onError]);

  if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY === 'pk_test_51QJxXx...') {
    return (
      <div className="stripe-error">
        {t('payment.notConfigured') || 'Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your environment variables.'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="stripe-payment-form">
        <div className="stripe-error">{error}</div>
      </div>
    );
  }

  if (isLoading || !clientSecret) {
    return (
      <div className="stripe-payment-form">
        <div className="stripe-loading">
          {t('payment.initializing') || 'Initializing payment...'}
        </div>
      </div>
    );
  }

  const returnUrl = `${window.location.origin}/cart?payment_return=1`;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#81D000',
            borderRadius: '8px'
          }
        }
      }}
    >
      <CheckoutForm
        amount={amount}
        paymentIntentId={paymentIntentId}
        returnUrl={returnUrl}
        restaurantId={restaurantId}
        onSuccess={onSuccess}
        onError={onError}
        restaurantName={restaurantName}
      />
    </Elements>
  );
};

export default StripePayment;
export { PAYMENT_RETURN_KEY };
